import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Compass, ChevronRight, Check, Target } from '../icons';
import { Badge } from '../ui/badge';
import type { AssignedPathway } from '../../types/pathway';

interface MyPathwaysProps {
  pathways: AssignedPathway[];
}

function ProgressRing({ progress, isComplete }: { progress: number; isComplete: boolean }) {
  const size = 52;
  const strokeWidth = 4;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <div className="relative flex items-center justify-center flex-shrink-0">
      <svg width={size} height={size} className="progress-ring">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          className={isComplete ? 'stroke-brand-10' : 'stroke-gray-10'}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          className={isComplete ? 'stroke-brand-40' : 'stroke-success-40'}
          style={{
            strokeDasharray: circumference,
            strokeDashoffset: offset,
            transition: 'stroke-dashoffset 0.6s ease',
          }}
        />
      </svg>
      <span className={`absolute text-xs font-semibold ${isComplete ? 'text-brand-60' : 'text-success-50'}`}>
        {progress}%
      </span>
    </div>
  );
}

function PathwayCard({ item, index }: { item: AssignedPathway; index: number }) {
  const progress = item.totalStages > 0
    ? Math.round((item.completedStages / item.totalStages) * 100)
    : 0;
  const isComplete = item.completedStages === item.totalStages && item.totalStages > 0;
  const notStarted = item.completedStages === 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 + index * 0.1, duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
    >
      <Link
        to={`/dashboard/pathway/${item.assignmentId}`}
        className="block bg-white rounded-3xl p-6 border border-gray-20 hover:shadow-soft-lg transition-all duration-300 group relative overflow-hidden"
      >
        {isComplete && (
          <div className="absolute top-4 right-4">
            <Badge variant="default" className="text-xs">
              <Check className="w-3.5 h-3.5 mr-1" />
              Complete
            </Badge>
          </div>
        )}

        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${
            isComplete
              ? 'bg-brand-10'
              : notStarted
              ? 'bg-gray-10'
              : 'bg-success-10'
          }`}>
            {isComplete ? (
              <Target className={`w-6 h-6 text-brand-60`} />
            ) : (
              <Compass className={`w-6 h-6 ${notStarted ? 'text-gray-40' : 'text-success-40'}`} />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="text-text-lg font-semibold text-gray-80 truncate group-hover:text-success-50 transition-colors">
              {item.pathway.name}
            </h3>
            {item.pathway.description && (
              <p className="text-paragraph-sm text-gray-50 mt-1 line-clamp-2">
                {item.pathway.description}
              </p>
            )}
          </div>
        </div>

        <div className="mt-5 flex items-center justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Badge variant={isComplete ? 'brand' : notStarted ? 'secondary' : 'default'} className="text-xs">
                {item.completedStages} / {item.totalStages} stages
              </Badge>
            </div>
            {item.currentStageTitle && !isComplete && (
              <p className="text-text-xs text-gray-40 flex items-center gap-1">
                <ChevronRight className="w-3 h-3" />
                Next: {item.currentStageTitle}
              </p>
            )}
          </div>

          <ProgressRing progress={progress} isComplete={isComplete} />
        </div>
      </Link>
    </motion.div>
  );
}

function EmptyState() {
  return (
    <div className="glass-effect rounded-3xl p-10 text-center">
      <div className="w-14 h-14 rounded-2xl bg-gray-10 flex items-center justify-center mx-auto mb-4">
        <Compass className="w-7 h-7 text-gray-40" />
      </div>
      <h3 className="text-text-lg font-semibold text-gray-80 mb-2">
        No pathways assigned yet
      </h3>
      <p className="text-paragraph-sm text-gray-50 max-w-sm mx-auto">
        Your coach will assign personalised growth pathways based on your assessment results.
      </p>
    </div>
  );
}

export function MyPathways({ pathways }: MyPathwaysProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2, duration: 0.5 }}
      className="mb-10"
    >
      <div className="mb-6">
        <Badge variant="default" className="mb-3">
          Pathways
        </Badge>
        <h2 className="text-2xl md:text-heading-sm font-bold text-gray-80">
          My Growth Pathways
        </h2>
        <p className="text-paragraph-md text-gray-50 mt-1">
          Continue where you left off
        </p>
      </div>

      {pathways.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid gap-6 md:grid-cols-2">
          {pathways.map((item, index) => (
            <PathwayCard key={item.assignmentId} item={item} index={index} />
          ))}
        </div>
      )}
    </motion.div>
  );
}
